import BasePage from './BasePage.js';

/**
 * 家族構成入力ページ
 */
export default class FamilyInputPage extends BasePage {
  render() {
    const html = this.#buildHTML();
    this._container.innerHTML = html;
    this._initializeButtonGroups();
    this.bindEvents();
  }

  bindEvents() {
    this.#bindSpouseEvents();
    this.#bindChildrenCountEvents();
    this.#bindChildrenStatusEvents();
    this.#bindGrandchildrenEvents();
    this.#bindParentsEvents();
    this.#bindSiblingsEvents();
    this.#bindNavigationEvents();
  }

  #buildHTML() {
    const progress = 66;
    const data = this._app.data;
    const { spouse, childrenCount, children, father, mother, siblingsCount } = data.family;

    // 第1順位相続人（子/孫）の有無を判定
    const hasFirstRankHeirs = children.some(child =>
      child.status === 'alive' || (child.status === 'deceased' && child.grandchildren > 0)
    );

    // 第2順位相続人（父母）の有無を判定
    const hasSecondRankHeirs = father === 'alive' || mother === 'alive';

    const childrenSections = this.#buildChildrenSections(childrenCount, children);
    const parentsSection = this.#buildParentsSection(father, mother, hasFirstRankHeirs);
    const parentsSelected = father !== null && mother !== null;
    const siblingsSection = this.#buildSiblingsSection(siblingsCount, hasFirstRankHeirs, hasSecondRankHeirs, parentsSelected);

    return `
      <div class="mb-8">
        <div class="progress h-10px mb-3">
          <div class="progress-bar bg-primary" role="progressbar" style="width: ${progress}%"
               aria-valuenow="${progress}" aria-valuemin="0" aria-valuemax="100"></div>
        </div>
      </div>

      <div class="text-center mb-8">
        <div class="d-flex align-items-center justify-content-center gap-3 mb-3">
          <span class="badge badge-circle badge-primary fs-4 fw-bold">2</span>
          <h3 class="fw-bold mb-0">家族構成の入力</h3>
        </div>
        <p class="mb-2">被相続人（亡くなった方）の家族関係を教えてください</p>
        <p class="fs-7 mb-0">配偶者の状態と子供の状態を選択してください</p>
      </div>

      <div class="mb-6">
        <div class="section-title-bg py-3 px-4 mb-3 fw-bold fs-6 mx-n3 mx-md-0 text-primary">配偶者<span class="text-danger ms-2">*</span></div>
        <div class="px-0">
          <div class="btn-group w-100" data-kt-buttons="true" data-kt-buttons-target="[data-kt-button]">
            <label class="btn btn-outline btn-active-primary ${spouse === 'alive' ? 'active' : ''}" data-kt-button="true">
              <input class="btn-check" type="radio" name="spouse" value="alive" ${spouse === 'alive' ? 'checked' : ''}/>
              健在
            </label>
            <label class="btn btn-outline btn-active-primary ${spouse === 'deceased' ? 'active' : ''}" data-kt-button="true">
              <input class="btn-check" type="radio" name="spouse" value="deceased" ${spouse === 'deceased' ? 'checked' : ''}/>
              故人
            </label>
            <label class="btn btn-outline btn-active-primary ${spouse === 'none' ? 'active' : ''}" data-kt-button="true">
              <input class="btn-check" type="radio" name="spouse" value="none" ${spouse === 'none' ? 'checked' : ''}/>
              なし
            </label>
          </div>
        </div>
      </div>

      <div class="mb-6">
        <div class="section-title-bg py-3 px-4 mb-3 fw-bold fs-6 mx-n3 mx-md-0 text-primary">
          子供の人数<span class="fs-7 fw-semibold ms-3" style="color: var(--bs-gray-700);">※養子や認知されたお子様を含みます</span>
        </div>
        <div class="px-0">
          <div class="fv-row mb-3">
            <select class="form-select form-select-solid" data-ref="childrenCountInput">
              ${[0,1,2,3,4,5].map(n => `<option value="${n}" ${childrenCount === n ? 'selected' : ''}>${n}人</option>`).join('')}
            </select>
          </div>
        </div>
      </div>

      ${childrenSections}

      ${parentsSection}

      ${siblingsSection}

      <div class="fixed-footer d-flex gap-3">
        <button type="button" class="btn btn-outline btn-outline-secondary" style="flex: 1 1 0;" data-ref="backBtn">
          <i class="ki-duotone ki-arrow-left fs-3 me-1">
            <span class="path1"></span>
            <span class="path2"></span>
          </i>
          戻る
        </button>
        <button type="button" class="btn btn-primary" style="flex: 1 1 0;" data-ref="nextBtn">
          次へ
          <i class="ki-duotone ki-arrow-right fs-3 ms-1">
            <span class="path1"></span>
            <span class="path2"></span>
          </i>
        </button>
      </div>
    `;
  }

  #buildParentsSection(father, mother, hasFirstRankHeirs) {
    // 第1順位相続人がいる場合は表示しない
    if (hasFirstRankHeirs) return '';

    // 両親の状態を4択UIの値にマッピング
    // 「故人/なし」は両方故人とnoneを統合
    let parentsStatus = null;
    if (father === 'alive' && mother === 'alive') {
      parentsStatus = 'both_alive';
    } else if (father === 'alive' && (mother === 'deceased' || mother === 'none')) {
      parentsStatus = 'father_only';
    } else if ((father === 'deceased' || father === 'none') && mother === 'alive') {
      parentsStatus = 'mother_only';
    } else if (father !== 'alive' && mother !== 'alive' && (father !== null || mother !== null)) {
      // 両方故人 or なし → 統合して 'none'
      parentsStatus = 'none';
    }

    return `
      <div class="mb-6" data-parents-section>
        <div class="section-title-bg py-3 px-4 mb-3 fw-bold fs-6 mx-n3 mx-md-0 text-primary">両親の状態</div>
        <div class="px-0">
          <div class="d-flex flex-wrap" data-kt-buttons="true" data-kt-buttons-target="[data-kt-button]" style="gap: 0;">
            <label class="btn btn-outline btn-active-primary ${parentsStatus === 'both_alive' ? 'active' : ''}" data-kt-button="true" style="width: 50%; border-radius: 0; border-bottom: 0; margin: 0;">
              <input class="btn-check" type="radio" name="parents" value="both_alive" ${parentsStatus === 'both_alive' ? 'checked' : ''}/>
              両親健在
            </label>
            <label class="btn btn-outline btn-active-primary ${parentsStatus === 'father_only' ? 'active' : ''}" data-kt-button="true" style="width: 50%; border-radius: 0; border-bottom: 0; border-left: 0; margin: 0;">
              <input class="btn-check" type="radio" name="parents" value="father_only" ${parentsStatus === 'father_only' ? 'checked' : ''}/>
              父のみ
            </label>
            <label class="btn btn-outline btn-active-primary ${parentsStatus === 'mother_only' ? 'active' : ''}" data-kt-button="true" style="width: 50%; border-radius: 0; margin: 0;">
              <input class="btn-check" type="radio" name="parents" value="mother_only" ${parentsStatus === 'mother_only' ? 'checked' : ''}/>
              母のみ
            </label>
            <label class="btn btn-outline btn-active-primary ${parentsStatus === 'none' ? 'active' : ''}" data-kt-button="true" style="width: 50%; border-radius: 0; border-left: 0; margin: 0;">
              <input class="btn-check" type="radio" name="parents" value="none" ${parentsStatus === 'none' ? 'checked' : ''}/>
              故人/なし
            </label>
          </div>
        </div>
      </div>
    `;
  }

  #buildSiblingsSection(siblingsCount, hasFirstRankHeirs, hasSecondRankHeirs, parentsSelected) {
    // 第1順位または第2順位の相続人がいる場合は表示しない
    // 両親が未選択（null）の場合も表示しない
    if (hasFirstRankHeirs || hasSecondRankHeirs || !parentsSelected) return '';

    return `
      <div class="mb-6" data-siblings-section>
        <div class="section-title-bg py-3 px-4 mb-3 fw-bold fs-6 mx-n3 mx-md-0 text-primary">
          健在の兄弟姉妹の人数<span class="fs-7 fw-semibold ms-3" style="color: var(--bs-gray-700);">※故人の兄弟姉妹は除く</span>
        </div>
        <div class="px-0">
          <div class="fv-row mb-3">
            <select class="form-select form-select-solid" data-ref="siblingsCountInput">
              ${[0,1,2,3,4,5].map(n => `<option value="${n}" ${siblingsCount === n ? 'selected' : ''}>${n}人</option>`).join('')}
            </select>
          </div>
        </div>
      </div>
    `;
  }

  #buildChildrenSections(childrenCount, children) {
    if (childrenCount === 0) return '';

    return Array.from({length: childrenCount}, (_, i) => {
      const child = children[i] || { status: 'alive', grandchildren: 0 };
      const showGrandchildren = child.status === 'deceased';
      const isLast = i === childrenCount - 1;

      return `
        <div class="${isLast ? 'mb-3' : 'mb-6'}">
          <div class="section-title-bg py-3 px-4 mb-3 fw-bold fs-6 mx-n3 mx-md-0 text-primary">子供${i + 1}</div>
          <div class="px-0">
            <div class="btn-group w-100 mb-3" data-kt-buttons="true" data-kt-buttons-target="[data-kt-button]">
              <label class="btn btn-outline btn-active-primary ${child.status === 'alive' ? 'active' : ''}" data-kt-button="true">
                <input class="btn-check" type="radio" name="child_${i}_status" value="alive" ${child.status === 'alive' ? 'checked' : ''}/>
                健在
              </label>
              <label class="btn btn-outline btn-active-primary ${child.status === 'deceased' ? 'active' : ''}" data-kt-button="true">
                <input class="btn-check" type="radio" name="child_${i}_status" value="deceased" ${child.status === 'deceased' ? 'checked' : ''}/>
                故人
              </label>
            </div>

            <div class="fv-row" data-grandchildren-section="${i}" style="display: ${showGrandchildren ? 'block' : 'none'};">
              <label class="form-label fs-6 fw-semibold mb-2">子供${i + 1}の子供（孫）の人数</label>
              <select class="form-select form-select-solid" data-child-grandchildren="${i}">
                ${[0,1,2,3,4,5].map(n => `<option value="${n}" ${child.grandchildren === n ? 'selected' : ''}>${n}人</option>`).join('')}
              </select>
            </div>
          </div>
        </div>
      `;
    }).join('');
  }

  #bindSpouseEvents() {
    const spouseInputs = this._container.querySelectorAll('input[name="spouse"]');
    spouseInputs.forEach(input => {
      input.addEventListener('change', () => {
        this._app.updateFamily({ spouse: input.value });
      });
    });
  }

  #bindChildrenCountEvents() {
    const childrenCountInput = this._container.querySelector('[data-ref="childrenCountInput"]');

    childrenCountInput?.addEventListener('change', () => {
      const newCount = parseInt(childrenCountInput.value);
      const newChildren = Array.from({ length: newCount }, () => ({ status: 'alive', grandchildren: 0 }));

      this._app.updateFamily({
        childrenCount: newCount,
        children: newChildren
      });

      this.render();
    });
  }

  #bindChildrenStatusEvents() {
    const childrenCount = this._app.data.family.childrenCount;

    for (let i = 0; i < childrenCount; i++) {
      const childStatusInputs = this._container.querySelectorAll(`input[name="child_${i}_status"]`);

      childStatusInputs.forEach(input => {
        input.addEventListener('change', () => {
          const children = [...this._app.data.family.children];
          children[i] = { ...children[i], status: input.value };

          // 健在に変更した場合は孫の人数を0にリセット
          if (input.value === 'alive') {
            children[i].grandchildren = 0;
          }

          this._app.updateFamily({ children });

          // 第1順位相続人の有無が変わる可能性があるため再レンダリング
          // （両親セクションの表示/非表示を更新）
          this.render();
        });
      });
    }
  }

  #bindGrandchildrenEvents() {
    const childrenCount = this._app.data.family.childrenCount;

    for (let i = 0; i < childrenCount; i++) {
      const grandchildrenSelect = this._container.querySelector(`[data-child-grandchildren="${i}"]`);

      grandchildrenSelect?.addEventListener('change', () => {
        const children = [...this._app.data.family.children];
        const oldGrandchildren = children[i].grandchildren || 0;
        const newGrandchildren = parseInt(grandchildrenSelect.value);
        children[i] = { ...children[i], grandchildren: newGrandchildren };
        this._app.updateFamily({ children });

        // 孫が0人⇔1人以上で第1順位相続人の有無が変わる可能性があるため再レンダリング
        // （両親セクションの表示/非表示を更新）
        if ((oldGrandchildren === 0 && newGrandchildren > 0) || (oldGrandchildren > 0 && newGrandchildren === 0)) {
          this.render();
        }
      });
    }
  }

  #bindParentsEvents() {
    const parentsInputs = this._container.querySelectorAll('input[name="parents"]');

    parentsInputs.forEach(input => {
      input.addEventListener('change', () => {
        // 4択UIの値をfather/motherの値にマッピング
        let father, mother;
        switch (input.value) {
          case 'both_alive':
            father = 'alive';
            mother = 'alive';
            break;
          case 'father_only':
            father = 'alive';
            mother = 'deceased';
            break;
          case 'mother_only':
            father = 'deceased';
            mother = 'alive';
            break;
          case 'none':
            // 「故人/なし」は両方故人として扱う（相続計算上は同じ）
            father = 'deceased';
            mother = 'deceased';
            break;
        }

        this._app.updateFamily({ father, mother });

        // 両親の状態が変わったら兄弟セクションの表示/非表示を更新
        this.render();
      });
    });
  }

  #bindSiblingsEvents() {
    const siblingsCountInput = this._container.querySelector('[data-ref="siblingsCountInput"]');

    siblingsCountInput?.addEventListener('change', () => {
      const newCount = parseInt(siblingsCountInput.value);
      this._app.updateFamily({ siblingsCount: newCount });
    });
  }

  #bindNavigationEvents() {
    const nextBtn = this._container.querySelector('[data-ref="nextBtn"]');
    const backBtn = this._container.querySelector('[data-ref="backBtn"]');

    nextBtn?.addEventListener('click', () => {
      if (this.validate()) {
        this._app.nextStep();
      }
    });

    backBtn?.addEventListener('click', () => {
      this._app.prevStep();
    });
  }

  validate() {
    const { spouse, childrenCount, children, father, mother, siblingsCount } = this._app.data.family;
    const errors = [];

    // 配偶者の状態チェック
    if (!spouse) {
      errors.push('・配偶者の状態を選択してください');
    }

    // 子供の状態チェック
    for (let i = 0; i < childrenCount; i++) {
      const child = children[i];
      if (!child || !child.status) {
        errors.push(`・子供${i + 1}の状態を選択してください`);
      }
    }

    // 第1順位相続人（子/孫）の有無を判定
    const hasFirstRankHeirs = children.some(child =>
      child.status === 'alive' || (child.status === 'deceased' && child.grandchildren > 0)
    );

    // 第1順位がいない場合、両親の状態チェック
    if (!hasFirstRankHeirs) {
      if (father === null || mother === null) {
        errors.push('・両親の状態を選択してください');
      }
    }

    // 第2順位相続人（父母）の有無を判定
    const hasSecondRankHeirs = father === 'alive' || mother === 'alive';

    // 第1順位も第2順位もいない場合、兄弟の入力が必要
    // （兄弟セクションが表示されている場合のみ）
    // siblingsCountは初期値0なので、特別なバリデーションは不要

    // 相続人が0人の場合のチェック
    const hasSpouseHeir = spouse === 'alive';
    const hasThirdRankHeirs = siblingsCount > 0;

    if (!hasSpouseHeir && !hasFirstRankHeirs && !hasSecondRankHeirs && !hasThirdRankHeirs) {
      errors.push('・相続人が0人です。配偶者、子供（孫）、両親、または兄弟姉妹が必要です');
    }

    // エラーがある場合は一括表示
    if (errors.length > 0) {
      alert('入力内容に誤りがあります:\n\n' + errors.join('\n'));
      return false;
    }

    return true;
  }
}
